/**
 * API Client
 * Axios instance with auth header injection and response unwrapping.
 */
import axios from 'axios';

// The API is a different origin from the portal.
//
// baseURL was '', so every request went to provider.smcompare.com, which serves
// the built SPA and nothing else. nginx answers unknown paths with index.html via
// try_files, so these calls did not even fail loudly -- axios got HTML with a 200
// and the unwrap below produced undefined, which the pages render as an empty
// state or, where they have one, as mock data. The portal looked like it worked.
//
// VITE_API_URL was already plumbed through: cd.yml passes it as a build arg and
// apps/provider-portal/Dockerfile declares ARG/ENV for it. Only this line was
// missing.
//
// The fallback stays empty on purpose. vite.config.js proxies /api to
// localhost:4000 in dev, so a relative base keeps dev requests same-origin and
// out of CORS; pointing it at localhost:4000 directly would make every dev
// request a cross-origin one from port 3003, which the API's dev allowlist does
// not include.
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor: inject auth token ──────────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('provider_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Response interceptor: unwrap { success, data } envelope ─────────────────
api.interceptors.response.use(
  (response) => {
    const body = response.data;
    if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
      return body.data;
    }
    return body;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('provider_token');
      window.location.href = '/login';
    }
    const message =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'An unexpected error occurred';
    return Promise.reject(new Error(message));
  }
);

/**
 * The payload from an api.* result, whatever shape it arrives in.
 *
 * The pages in this app disagree about what api.get() returns: some read
 * `res.data.data` (an axios response), some read `res.success` (the raw body),
 * and some use the result directly (the unwrapped payload). All three cannot be
 * right at once, and the response interceptor above satisfies only the third --
 * evidence that none of this had run against a live API, which it could not,
 * because baseURL was ''.
 *
 * Settling that disagreement means auditing every call site against its
 * endpoint's shape. Until then, new code should go through this, which is
 * correct under all three and stays correct if the interceptor is changed.
 */
export function payloadOf(result) {
  // An axios response has both; an unwrapped payload that happens to carry a
  // `data` key does not have `status`.
  const body = result && result.status !== undefined && result.data !== undefined
    ? result.data
    : result;

  if (body && typeof body === 'object' && !Array.isArray(body) && 'data' in body) {
    return body.data;
  }
  return body;
}

export default api;
